import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, Typogaphy } from 'vnetwork-ui'
import {
    Download,
    ImageDown,
    TrendingUp,
    TrendingDown,
    Lightbulb,
} from 'lucide-react'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import * as XLSX from 'xlsx'
import { ServiceMonthlyReportItem } from '../types'
import { SCHEMES } from '@/components/EchartCustomized'

interface ServicesTrendChartProps {
    data: ServiceMonthlyReportItem[]
    previousYearData?: ServiceMonthlyReportItem[]
    showComparison: boolean
    year: number
}

const COLORS = {
    starting: SCHEMES.mono.gray[2],
    new: SCHEMES.success[0],
    cancelled: SCHEMES.error[0],
    ending: SCHEMES.info[0],
    prevEnding: SCHEMES.info[2],
}

const ServicesTrendChart: React.FC<ServicesTrendChartProps> = ({
    data,
    previousYearData,
    showComparison,
    year,
}) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)

    const chartData = useMemo(() => {
        return data.map((item) => {
            const prevItem = previousYearData?.find(
                (p) => p.month.slice(5) === item.month.slice(5)
            )
            return {
                month: item.month,
                monthLabel: dayjs(item.month + '-01').format('MMM'),
                starting_services: item.starting_services,
                new_services: item.new_services,
                cancelled_services: -item.cancelled_services, // Negative for better visualization
                ending_services: item.ending_services,
                prev_ending_services: prevItem?.ending_services,
            }
        })
    }, [data, previousYearData])

    // Analysis data
    const analysis = useMemo(() => {
        if (data.length < 2) return null

        const totalNew = data.reduce((s, d) => s + d.new_services, 0)
        const totalCancelled = data.reduce(
            (s, d) => s + d.cancelled_services,
            0
        )
        const netChange =
            data[data.length - 1].ending_services - data[0].starting_services

        // Find best/worst months
        const sortedByNew = [...data].sort(
            (a, b) => b.new_services - a.new_services
        )
        const sortedByCancelled = [...data].sort(
            (a, b) => b.cancelled_services - a.cancelled_services
        )

        // Trend direction (compare last 3 months vs first 3 months)
        const firstHalf = data.slice(0, Math.ceil(data.length / 2))
        const secondHalf = data.slice(Math.ceil(data.length / 2))
        const firstHalfAvg =
            firstHalf.reduce((s, d) => s + d.ending_services, 0) /
            firstHalf.length
        const secondHalfAvg =
            secondHalf.reduce((s, d) => s + d.ending_services, 0) /
            secondHalf.length
        const trendDirection =
            secondHalfAvg > firstHalfAvg
                ? 'up'
                : secondHalfAvg < firstHalfAvg
                  ? 'down'
                  : 'stable'

        // YoY comparison
        let yoyGrowth = null
        if (previousYearData && previousYearData.length > 0) {
            const prevTotal =
                previousYearData[previousYearData.length - 1]
                    ?.ending_services || 0
            const currTotal = data[data.length - 1].ending_services
            if (prevTotal > 0) {
                yoyGrowth = ((currTotal - prevTotal) / prevTotal) * 100
            }
        }

        return {
            totalNew,
            totalCancelled,
            netChange,
            bestNewMonth: sortedByNew[0],
            worstCancelMonth: sortedByCancelled[0],
            trendDirection,
            yoyGrowth,
        }
    }, [data, previousYearData])

    const exportToImage = async () => {
        if (!chartRef.current) return
        const canvas = await html2canvas(chartRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
        })
        const link = document.createElement('a')
        link.download = `service_trend_${year}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
    }

    const exportToExcel = () => {
        const exportData = data.map((item) => {
            const prevItem = previousYearData?.find(
                (p) => p.month.slice(5) === item.month.slice(5)
            )
            return {
                [t('month')]: dayjs(item.month + '-01').format('MMMM YYYY'),
                [t('starting_services')]: item.starting_services,
                [t('new_services')]: item.new_services,
                [t('cancelled_services')]: item.cancelled_services,
                [t('ending_services')]: item.ending_services,
                ...(showComparison && prevItem
                    ? {
                          [`${t('ending_services')} ${year - 1}`]:
                              prevItem.ending_services,
                          [t('yoy_change')]:
                              item.ending_services - prevItem.ending_services,
                      }
                    : {}),
            }
        })

        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(exportData)
        XLSX.utils.book_append_sheet(wb, ws, t('service_trend'))
        XLSX.writeFile(wb, `service_trend_${year}.xlsx`)
    }

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null

        // Get the original month from chartData payload
        const chartItem = payload[0]?.payload
        const originalMonth = chartItem?.month

        const monthData = data.find((d) => d.month === originalMonth)
        const prevMonthData = previousYearData?.find(
            (p) => p.month.slice(5) === originalMonth?.slice(5)
        )

        return (
            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                <p className="font-semibold text-gray-800 mb-2">
                    {dayjs(originalMonth + '-01').format('MMMM YYYY')}
                </p>
                <div className="space-y-1 text-sm">
                    <p className="text-slate-500">
                        {t('starting_services')}:{' '}
                        <span className="font-medium">
                            {monthData?.starting_services.toLocaleString()}
                        </span>
                    </p>
                    <p
                        className={
                            monthData?.new_services
                                ? 'text-green-600'
                                : 'text-gray-400'
                        }
                    >
                        {t('new_services')}:{' '}
                        <span className="font-medium">
                            {monthData?.new_services
                                ? `+${monthData.new_services.toLocaleString()}`
                                : '-'}
                        </span>
                    </p>
                    <p
                        className={
                            monthData?.cancelled_services
                                ? 'text-red-600'
                                : 'text-gray-400'
                        }
                    >
                        {t('cancelled_services')}:{' '}
                        <span className="font-medium">
                            {monthData?.cancelled_services
                                ? `-${monthData.cancelled_services.toLocaleString()}`
                                : '-'}
                        </span>
                    </p>
                    <p className="text-blue-600 font-medium">
                        {t('ending_services')}:{' '}
                        <span className="font-bold">
                            {monthData?.ending_services.toLocaleString()}
                        </span>
                    </p>
                    {showComparison && prevMonthData && (
                        <div className="border-t border-gray-200 pt-1 mt-1">
                            <p className="text-blue-400">
                                {t('previous_year')}:{' '}
                                <span className="font-medium">
                                    {prevMonthData.ending_services.toLocaleString()}
                                </span>
                            </p>
                            <p
                                className={
                                    monthData!.ending_services -
                                        prevMonthData.ending_services >=
                                    0
                                        ? 'text-green-600'
                                        : 'text-red-600'
                                }
                            >
                                {t('yoy_change')}:{' '}
                                <span className="font-medium">
                                    {monthData!.ending_services -
                                        prevMonthData.ending_services >=
                                    0
                                        ? '+'
                                        : ''}
                                    {(
                                        monthData!.ending_services -
                                        prevMonthData.ending_services
                                    ).toLocaleString()}
                                </span>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div
            ref={chartRef}
            className="bg-white rounded-md p-4 shadow-sm border border-gray-100 h-full"
        >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex flex-col gap-1">
                    <Typogaphy className="text-primary font-semibold line-clamp-1">
                        {t('monthly_service_trend')}
                    </Typogaphy>
                    <Typogaphy className="text-xs text-gray-500 line-clamp-1">
                        {t('service_flow_by_month')}
                    </Typogaphy>
                </div>
                <div className="flex gap-1 justify-end">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportToImage}
                        className="!min-w-0 !px-2"
                        title={t('download_image')}
                    >
                        <ImageDown size={16} />
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportToExcel}
                        className="!min-w-0 !px-2"
                        title={t('download_excel')}
                    >
                        <Download size={16} />
                    </Button>
                </div>
            </div>

            {chartData.length === 0 ? (
                <div className="flex justify-center items-center h-[350px] text-gray-500">
                    {t('no_data_available')}
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                            dataKey="monthLabel"
                            stroke="#9ca3af"
                            fontSize={12}
                        />
                        <YAxis stroke="#9ca3af" fontSize={12} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            content={({ payload }) => (
                                <div className="flex justify-center gap-6 mt-2">
                                    {payload?.map((entry, index) => (
                                        <div
                                            key={`legend-${index}`}
                                            className="flex items-center gap-2"
                                        >
                                            {entry.dataKey ===
                                            'prev_ending_services' ? (
                                                // Dashed line icon for previous year
                                                <svg
                                                    width="20"
                                                    height="12"
                                                    viewBox="0 0 20 12"
                                                >
                                                    <line
                                                        x1="0"
                                                        y1="6"
                                                        x2="20"
                                                        y2="6"
                                                        stroke={entry.color}
                                                        strokeWidth="2"
                                                        strokeDasharray="4 2"
                                                    />
                                                </svg>
                                            ) : entry.type === 'line' ? (
                                                // Solid line icon
                                                <svg
                                                    width="20"
                                                    height="12"
                                                    viewBox="0 0 20 12"
                                                >
                                                    <line
                                                        x1="0"
                                                        y1="6"
                                                        x2="20"
                                                        y2="6"
                                                        stroke={entry.color}
                                                        strokeWidth="3"
                                                    />
                                                </svg>
                                            ) : (
                                                // Square icon for bars
                                                <div
                                                    className="w-3 h-3 rounded-sm"
                                                    style={{
                                                        backgroundColor:
                                                            entry.color,
                                                    }}
                                                />
                                            )}
                                            <span className="text-sm text-gray-600">
                                                {entry.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        />

                        {/* New services (positive bar) */}
                        <Bar
                            dataKey="new_services"
                            name={t('new_services')}
                            fill={COLORS.new}
                            radius={[4, 4, 0, 0]}
                            stackId="stack"
                        />

                        {/* Cancelled services (negative bar) */}
                        <Bar
                            dataKey="cancelled_services"
                            name={t('cancelled_services')}
                            fill={COLORS.cancelled}
                            radius={[0, 0, 4, 4]}
                            stackId="stack"
                        />

                        {/* Ending services line */}
                        <Line
                            type="monotone"
                            dataKey="ending_services"
                            name={t('ending_services')}
                            stroke={COLORS.ending}
                            strokeWidth={3}
                            dot={{ fill: COLORS.ending, strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6 }}
                        />

                        {/* Previous year comparison line */}
                        {showComparison && previousYearData && (
                            <Line
                                type="monotone"
                                dataKey="prev_ending_services"
                                name={`${t('ending_services')} ${year - 1}`}
                                stroke={COLORS.prevEnding}
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={{
                                    fill: COLORS.prevEnding,
                                    strokeWidth: 1,
                                    r: 3,
                                }}
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            )}

            {/* Analysis Section */}
            {analysis && (
                <div
                    className="mt-4 pt-4 border-t border-gray-100"
                    data-tour="service-trend-analysis"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Lightbulb size={16} className="text-amber-500" />
                        <Typogaphy className="text-sm font-semibold text-gray-700">
                            {t('reports.analysis.key_insights')}
                        </Typogaphy>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="p-2 bg-gray-50 rounded-lg">
                            <Typogaphy className="text-xs text-gray-500">
                                {t('reports.analysis.net_change')}
                            </Typogaphy>
                            <div className="flex items-center gap-1 mt-1">
                                {analysis.netChange >= 0 ? (
                                    <TrendingUp
                                        size={14}
                                        className="text-green-600"
                                    />
                                ) : (
                                    <TrendingDown
                                        size={14}
                                        className="text-red-600"
                                    />
                                )}
                                <Typogaphy
                                    className={`font-semibold ${analysis.netChange >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                >
                                    {analysis.netChange >= 0 ? '+' : ''}
                                    {analysis.netChange}
                                </Typogaphy>
                            </div>
                        </div>
                        <div className="p-2 bg-green-50 rounded-lg">
                            <Typogaphy className="text-xs text-gray-500">
                                {t('reports.analysis.best_new_month')}
                            </Typogaphy>
                            <Typogaphy className="font-semibold text-green-700 mt-1">
                                {dayjs(
                                    analysis.bestNewMonth.month + '-01'
                                ).format('MMM')}{' '}
                                (+{analysis.bestNewMonth.new_services})
                            </Typogaphy>
                        </div>
                        <div className="p-2 bg-red-50 rounded-lg">
                            <Typogaphy className="text-xs text-gray-500">
                                {t('reports.analysis.highest_cancel_month')}
                            </Typogaphy>
                            <Typogaphy className="font-semibold text-red-700 mt-1">
                                {dayjs(
                                    analysis.worstCancelMonth.month + '-01'
                                ).format('MMM')}{' '}
                                (-{analysis.worstCancelMonth.cancelled_services}
                                )
                            </Typogaphy>
                        </div>
                        {showComparison && analysis?.yoyGrowth !== null && (
                            <div className="p-2 bg-blue-50 rounded-md h-full">
                                <Typogaphy className="text-xs text-gray-500">
                                    YoY
                                </Typogaphy>
                                <div className="flex items-center gap-1 mt-1">
                                    {analysis?.yoyGrowth >= 0 ? (
                                        <TrendingUp
                                            size={14}
                                            className="text-blue-600"
                                        />
                                    ) : (
                                        <TrendingDown
                                            size={14}
                                            className="text-blue-600"
                                        />
                                    )}
                                    <Typogaphy className="font-semibold text-blue-700">
                                        {analysis.yoyGrowth >= 0 ? '+' : ''}
                                        {analysis.yoyGrowth.toFixed(1)}%
                                    </Typogaphy>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ServicesTrendChart
